import React, { useMemo } from 'react';
import { Box, Skeleton, Stack } from '@mui/material';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import useCustomError from 'hooks/useCustomError';
import type { ConditionRating } from 'types/conditionRating';
import type { LiveWindPoint } from 'types/liveWind';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { WindData } from 'types/wind';
import type { WindTooltipData } from 'types/graphs';

import WindForecastContainer from './components/WindForecastContainer';
import GraphLegend from './components/GraphLegend';

import styles from './ForecastGraphWind.module.scss';

interface ForecastGraphWindContentProps {
  activeItem: {
    day: number;
    hour: number;
    condition?: ConditionRating;
    timestamp?: number;
  };
  daysToDisplay: Array<number>;
  liveWind: Array<LiveWindPoint> | null;
  wind: WindData;
  loading: boolean;
  sunlightTimes: SunlightTimes;
  isMobile: boolean;
  activeWind?: WindTooltipData;
}

const ForecastGraphWindContent: React.FC<ForecastGraphWindContentProps> = ({
  activeItem,
  liveWind,
  wind,
  loading,
  daysToDisplay,
  sunlightTimes,
  isMobile,
  activeWind,
}) => {
  const hasLiveWind = useMemo(
    () =>
      // Check if there is live wind data, regardless of active day
      !!liveWind?.length,
    [liveWind],
  );

  // Get the isLiveCurrently property directly from the tooltip data when available
  const isCurrentWindLive = useMemo(() => {
    // Always inactive for future days
    if (activeItem.day > 0) return false;

    // Get the isLiveCurrently property directly from the tooltip data when available
    if (activeWind?.data?.isLiveCurrently !== undefined) {
      return activeWind.data.isLiveCurrently;
    }

    // Otherwise determine if the current wind is live based on current time
    if (!hasLiveWind) return false;

    const currentTimestamp = Date.now();
    const activeTimestamp = activeItem.timestamp
      ? new Date(activeItem.timestamp * 1000).getTime()
      : null;

    // Wind is live if we're looking at today (day 0) and the active timestamp is now or in the past
    return activeTimestamp ? activeTimestamp <= currentTimestamp : false;
  }, [activeWind, hasLiveWind, activeItem]);

  if (loading) {
    return (
      <Stack direction="column">
        <Skeleton
          animation="wave"
          data-testid="wind-graph-loading"
          variant="rectangular"
          height={60}
          width="100%"
        />
        <Skeleton
          animation="wave"
          data-testid="wind-graph-data-loading"
          variant="rectangular"
          sx={{ marginTop: '1px' }}
          height={99}
          width="100%"
        />
      </Stack>
    );
  }

  return (
    <Box className={styles.forecastGraphWindContainer} data-testid="wind-graph">
      <Stack direction="row" spacing={0} width="100%">
        {(wind?.days || liveWind) &&
          daysToDisplay.map((day: number, displayOrderIndex: number) => (
            <WindForecastContainer
              activeItem={activeItem}
              liveWind={liveWind}
              wind={wind}
              key={`${day}-${daysToDisplay.join('')}`}
              day={day}
              displayOrderIndex={displayOrderIndex}
              sunlightTimes={sunlightTimes}
              isLive={!!liveWind?.length && day === 0}
              isMobile={isMobile}
            />
          ))}
      </Stack>
      <Box className={styles.legendContainer}>
        <GraphLegend
          dataTestId="wind-graph-legend"
          hasLiveWind={hasLiveWind}
          isCurrentWindLive={isCurrentWindLive}
        />
      </Box>
    </Box>
  );
};

export const ForecastGraphWind: React.FC<ForecastGraphWindContentProps> = (props) => {
  const {
    wind: { error },
  } = props;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-graph-wind"
      render={() => (
        <ErrorMessage dataTestId="wind-graph-error" Icon={ErrorIcon} message={errorMessage} />
      )}
    >
      <ForecastGraphWindContent {...props} />
    </ErrorBoundary>
  );
};

export default ForecastGraphWind;
